import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Check, MessageCircle, FileDown } from "lucide-react";
import api, { API } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const { openQuote, config } = useSite();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [slug]);

  if (!data) return <div className="mx-auto max-w-[1400px] px-4 py-32 text-[#A1A1AA] lg:px-8">Carregando...</div>;
  if (data.error) return <div className="mx-auto max-w-[1400px] px-4 py-32 lg:px-8"><h1 className="font-heading text-2xl text-white">Produto não encontrado</h1></div>;

  const { product: p, category, related } = data;
  const waMsg = encodeURIComponent(`Olá! Tenho interesse no equipamento: ${p.name}.`);

  return (
    <div>
      <div className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8">
        <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
          <Link to="/equipamentos" className="hover:text-white">Equipamentos</Link>
          <ChevronRight className="h-3 w-3" />
          {category && <><Link to={`/equipamentos/${category.slug}`} className="hover:text-white">{category.name}</Link><ChevronRight className="h-3 w-3" /></>}
          <span className="text-white">{p.name}</span>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 pb-16 lg:grid-cols-2 lg:px-8">
        <div className="relative self-start border border-[#27272A] bg-black">
          <img src={p.image} alt={p.name} className="aspect-[3/2] w-full object-cover" />
          <div className="absolute left-4 top-4"><StatusBadge status={p.status} /></div>
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black text-white md:text-4xl">{p.name}</h1>
          <p className="mt-4 text-[#A1A1AA]">{p.description}</p>

          {p.highlights?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {p.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm text-white"><Check className="h-4 w-4 text-[#FF5500]" />{h}</li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="product-quote-button" onClick={() => openQuote({ equipment_type: category?.name || p.name, product_slug: p.slug, source: "produto" })}
              className="rounded-none bg-[#FF5500] px-7 py-6 text-base font-bold hover:bg-[#CC4400]">
              Solicitar Orçamento
            </Button>
            <a href={`https://wa.me/${config?.whatsapp || "5547999999999"}?text=${waMsg}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-none border-[#3f3f46] bg-transparent px-7 py-6 text-base font-bold text-white hover:bg-[#1E1F22] hover:text-white">
                <MessageCircle className="mr-2 h-4 w-4" /> Falar com Técnico
              </Button>
            </a>
          </div>

          <div className="mt-10 border border-[#27272A]">
            <div className="flex items-center justify-between border-b border-[#27272A] bg-[#151618] px-5 py-3">
              <span className="label-eyebrow">Especificações Técnicas</span>
              <span className="text-[0.7rem] font-bold uppercase tracking-wider text-yellow-400">Sob dimensionamento</span>
            </div>
            <table className="w-full">
              <tbody>
                {p.specs?.map((s, i) => (
                  <tr key={i} className="border-b border-[#27272A] last:border-0">
                    <td className="px-5 py-3 text-sm text-[#A1A1AA]">{s.label}</td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-white">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 flex items-center gap-2 text-xs text-[#71717A]">
            <FileDown className="h-3.5 w-3.5" /> Especificações finais confirmadas na proposta técnica após dimensionamento.
          </p>
          <a href={`${API}/products/${p.slug}/sheet.pdf`} target="_blank" rel="noopener noreferrer" data-testid="product-sheet-pdf" className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#FF5500] hover:underline">
            <FileDown className="h-4 w-4" /> Baixar ficha técnica (PDF)
          </a>
        </div>
      </div>

      {related?.length > 0 && (
        <div className="border-t border-[#27272A] bg-[#0A0A0B]">
          <div className="mx-auto max-w-[1400px] px-4 py-16 lg:px-8">
            <h2 className="font-heading mb-8 text-2xl font-bold text-white">Produtos relacionados</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => <ProductCard key={r.slug} product={r} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
